import React, { useEffect, useState } from 'react';
import { merge, set, omit, get } from 'lodash';
import { Grid } from '@mui/material';
import {
  Section,
  Input,
  Select,
  Layout,
  StateSelect,
  SignupStepContainer,
  ErrorDialog,
  Button,
  Radio,
} from 'components';
import { captureError } from 'utils/sentry';
import states from 'utils/us-states';
import { useGlobal } from 'context';
import './account.less';
import { request } from 'utils/api';
import { PUBLIC_TOKEN } from 'utils/env';
import { typeToFormErrors } from 'utils/validate';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_KEY } from 'utils/env';
import TermsAndConditions from './TermsAndConditions';
import MessagingPolicy from './MessagingPolicy';
import { Header } from 'components/SignupStepContainer';
import { withLayout } from 'helpers';
import { getLandingPageVariantString } from 'screens/TalkToUs/utils';
import { useFeatures, useFormVariant } from 'components/FeaturesContext';
import { useTracker } from 'utils/tracking';
import { PhoneNumberInput } from 'components/PhoneNumberInput';
import { mapMultiSalesforceToSingle } from './utils';

const billingAddress = yup
  .object()
  .shape({
    city: yup.string().required(),
    state: yup.string().required(),
    street1: yup.string().required(),
    street2: yup.string(),
    zipCode: yup.string().required(),
  })
  .required();

const accountSchema = yup.object().shape({
  acceptedTerms: yup
    .boolean()
    .oneOf([true], 'This field must be checked')
    .required(),
  textMessageOptIn: yup
    .boolean()
    .oneOf([true], 'This field must be checked')
    .required(),
  email: yup.string().email().required(),
  firstName: yup.string().required(),
  gender: yup.string().required('Gender is required'),
  lastName: yup.string().required(),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(
      /^(?!(0))^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      {
        message: 'Must be a valid US phone number.',
        excludeEmptyString: true,
      }
    )
    .min(10, 'Phone number is too short')
    .max(10, 'Phone number is too long'),
});

const freeUserSignUpAccountSchema = accountSchema.concat(
  yup.object().shape({
    password: yup
      .string()
      .matches(
        /^(?=.*[A-Z])(?=.*\d).{8,}$/,
        '8 characters, a number and an uppercase char'
      )
      .required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
    captcha: yup.string().required('Please complete the captcha to continue.'),
  })
);

const freeUserCheckoutSchema = yup
  .object()
  .shape({
    shippingAddress: yup
      .object()
      .shape({
        city: yup.string().trim().required('City is required'),
        state: yup.string().trim().required('State is required'),
        street1: yup.string().trim().required('Street 1 is required'),
        street2: yup.string(),
        zipCode: yup.string().trim().required('Zip code is required'),
      })
      .required(),
  })
  .when('$sameAsBilling', (sameAsBilling, schema) =>
    !sameAsBilling ? schema.shape({ billingAddress }) : schema
  );

function FormInput({
  flowType,
  fields,
  fieldsErrors,
  setField,
  sameAsBilling,
  setSameAsBilling,
  showSmsOptIn,
}) {
  return flowType === 'free-user-checkout' ? (
    <FreeUserCheckoutInputs
      fields={fields}
      fieldsErrors={fieldsErrors}
      setField={setField}
      sameAsBilling={sameAsBilling}
      setSameAsBilling={setSameAsBilling}
    />
  ) : (
      <AccountInputs
        flowType={flowType}
        fields={fields}
        fieldsErrors={fieldsErrors}
        setField={setField}
        showSmsOptIn={showSmsOptIn}
      />
    );
}

function FreeUserCheckoutInputs({
  fields,
  fieldsErrors,
  setField,
  sameAsBilling,
  setSameAsBilling,
}) {
  const { availableStates } = useGlobal();
  const isAllowedState = availableStates.includes(
    get(fields, 'billingAddress.state')
  );

  const shippingStates = [
    ...Object.keys(states)
      .filter((state) => availableStates.includes(state))
      .map((value) => {
        return { value, text: states[value] };
      }),
    { value: 'NY', text: 'New York' },
  ];

  return (
    <>
      {' '}
      <h1 className="h1">Your shipping address</h1>{' '}
      <Layout horizontal stackable>
        {' '}
        <Grid container spacing={2}>
          {' '}
          <Grid item xs={6}>
            <Input
              required
              label="Street address #1"
              name="shippingAddress.street1"
              error={get(fieldsErrors, 'shippingAddress.street1')}
              value={get(fields, 'shippingAddress.street1')}
              onChange={(_e, { name, value }) => setField(name, value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              label="Street address #2"
              name="shippingAddress.street2"
              error={get(fieldsErrors, 'shippingAddress.street2')}
              value={get(fields, 'shippingAddress.street2')}
              onChange={(_e, { name, value }) => setField(name, value)}
            />
          </Grid>
        </Grid>
      </Layout>
      <Layout horizontal stackable>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Input
              required
              label="City"
              name="shippingAddress.city"
              error={get(fieldsErrors, 'shippingAddress.city')}
              value={get(fields, 'shippingAddress.city')}
              onChange={(_e, { name, value }) => setField(name, value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              required
              label="Zip"
              name="shippingAddress.zipCode"
              error={get(fieldsErrors, 'shippingAddress.zipCode')}
              value={get(fields, 'shippingAddress.zipCode')}
              onChange={(_e, { name, value }) => setField(name, value)}
            />
          </Grid>
        </Grid>
      </Layout>
      <Select
        options={shippingStates}
        label="State"
        name="shippingAddress.state"
        error={get(fieldsErrors, 'shippingAddress.state')}
        value={get(fields, 'shippingAddress.state')}
        onChange={(_e, { name, value }) => setField(name, value)}
      />
      <h1 className="h1">Your billing address</h1>
      {isAllowedState && (
        <Radio
          checked={sameAsBilling}
          onChange={(_e, { checked }) => setSameAsBilling(checked)}
          label="Same as shipping address"
          type="checkbox"
        />
      )}
      {(!sameAsBilling || !isAllowedState) && (
        <>
          <Layout horizontal stackable>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Input
                  label="Street address #1"
                  name="billingAddress.street1"
                  value={get(fields, 'billingAddress.street1')}
                  error={get(fieldsErrors, 'billingAddress.street1')}
                  onChange={(_e, { name, value }) => setField(name, value)}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  label="Street address #2"
                  name="billingAddress.street2"
                  value={get(fields, 'billingAddress.street2')}
                  error={get(fieldsErrors, 'billingAddress.street2')}
                  onChange={(_e, { name, value }) => setField(name, value)}
                />
              </Grid>
            </Grid>
          </Layout>

          <Layout horizontal stackable>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Input
                  label="City"
                  name="billingAddress.city"
                  value={get(fields, 'billingAddress.city')}
                  error={get(fieldsErrors, 'billingAddress.city')}
                  onChange={(_e, { name, value }) => setField(name, value)}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  label="Zip"
                  name="billingAddress.zipCode"
                  value={get(fields, 'billingAddress.zipCode')}
                  error={get(fieldsErrors, 'billingAddress.zipCode')}
                  onChange={(_e, { name, value }) => setField(name, value)}
                />
              </Grid>
            </Grid>
          </Layout>

          <StateSelect
            name="billingAddress.state"
            label="State"
            value={get(fields, 'billingAddress.state')}
            error={get(fieldsErrors, 'billingAddress.state')}
            onChange={(_e, { name, value }) => setField(name, value)}
          />
        </>
      )}
    </>
  );
}

function AccountInputs({
  flowType,
  fields,
  fieldsErrors,
  setField,
  showSmsOptIn,
}) {
  const { lockCheckoutOptions } = useGlobal();
  return (
    <>
      <Layout horizontal stackable style={{ marginBottom: '0.6em' }}>
        <Select
          label="Gender"
          required
          name="gender"
          error={fieldsErrors.gender}
          value={fields.gender}
          disabled={lockCheckoutOptions}
          onChange={(_e, { name, value }) => setField(name, value)}
          options={[
            { value: '', text: 'Your gender' },
            { value: 'MALE', text: 'Male' },
            { value: 'FEMALE', text: 'Female' },
            { value: 'OTHER', text: 'Other' },
            { value: 'DECLINE_TO_SPECIFY', text: 'Decline to specify' },
          ]}
        />
        <div style={{ width: '100%', marginRight: '1em' }}></div>
        {/* This is to center the gender select */}
      </Layout>
      <Layout horizontal stackable>
        <Input
          required
          error={fieldsErrors.firstName}
          label="First Name"
          name="firstName"
          value={fields.firstName}
          disabled={lockCheckoutOptions}
          onChange={(_e, { name, value }) => setField(name, value)}
        />
        <Input
          required
          label="Last Name"
          name="lastName"
          error={fieldsErrors.lastName}
          value={fields.lastName}
          disabled={lockCheckoutOptions}
          onChange={(_e, { name, value }) => setField(name, value)}
        />
      </Layout>
      <Layout horizontal stackable>
        <Input
          required
          label="Email"
          type="email"
          name="email"
          error={fieldsErrors.email}
          value={fields.email}
          disabled={lockCheckoutOptions}
          onChange={(_e, { name, value }) => setField(name, value)}
        />
        <PhoneNumberInput
          required={!lockCheckoutOptions}
          disabled={lockCheckoutOptions}
          label="Phone number"
          type="tel"
          error={fieldsErrors.phone}
          name="phone"
          value={fields.phone}
          onChange={({ name, value }) => setField(name, value)}
        />
      </Layout>

      {flowType === 'free-user-sign-up' && (
        <Layout horizontal stackable>
          <Input
            required
            label="Password"
            type="password"
            name="password"
            error={fieldsErrors.password}
            value={fields.password}
            onChange={(_e, { name, value }) => setField(name, value)}
          />
          <Input
            required
            label="Confirm password"
            type="password"
            error={fieldsErrors.confirmPassword}
            name="confirmPassword"
            value={fields.confirmPassword}
            onChange={(_e, { name, value }) => setField(name, value)}
          />
        </Layout>
      )}

      <TermsAndConditions
        hasAcceptedTerms={fields.acceptedTerms}
        setField={(newValue) => setField('acceptedTerms', newValue)}
        fieldsErrors={fieldsErrors}
      />

      {showSmsOptIn && (
        <MessagingPolicy
          hasTextMessageOptIn={fields.textMessageOptIn}
          setField={(newValue) => setField('textMessageOptIn', newValue)}
          fieldsErrors={fieldsErrors}
        />
      )}
    </>
  );
}

function Account(props) {
  const context = useGlobal();
  const { CAPTURE_PHONE_FOR_PRE_LEADS: requestPhoneNumber } = useFeatures();
  const _formVariant = useFormVariant();
  const trackOnce = useTracker();

  const [error, setError] = useState();
  const [fields, setFields] = useState({
    acceptedTerms: false,
    email: '',
    firstName: '',
    gender: '',
    lastName: '',
    phone: '',
    state: context.startInfo?.state,
    shippingAddress: {
      state: context.startInfo?.state,
    },
    password: '',
    confirmPassword: '',
    captcha: '',
    textMessageOptIn: requestPhoneNumber ? true : false,
    ...context.account,
  });
  const [fieldsErrors, setFieldsErrors] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [loading, setLoading] = useState(false);

  const flowType = props.flow || window.sessionStorage.getItem('flow');

  useEffect(async () => {
    window.scrollTo({ top: 0 });
    window?.gtag('event', 'view_account_form');
  }, []);

  function onCaptchaCompletion(captcha) {
    setField('captcha', captcha);
  }

  async function handleOnSubmit(e) {
    e?.preventDefault();
    const { leadQuestions } = context;
    const body = fields;

    trackOnce('lead_question20_Account_creation_book');
    if (['reset', 'you'].includes(props.plan?.id)) {
      trackOnce(`lead_question20_Account_creation_${props.plan?.id}`);
    }

    try {
      let schemaToValidate = accountSchema;
      if (flowType === 'free-user-sign-up')
        schemaToValidate = freeUserSignUpAccountSchema;
      if (flowType === 'free-user-checkout')
        schemaToValidate = freeUserCheckoutSchema;

      await schemaToValidate.validate(body, {
        abortEarly: false,
        context: { sameAsBilling },
      });
    } catch (e) {
      console.log(e);
      setFieldsErrors(typeToFormErrors(e));
      return;
    }

    setError(undefined);
    setLoading(true);
    const cleanBody = omit({ ...body }, ['confirmPassword']);

    try {
      const flagQuestions = context.flagQuestions.filter(
        (c) => c.answer?.length
      );
      let singleQuestions = leadQuestions.filter((q) => !q.multiSalesforce);
      let questions = singleQuestions
        .filter((c) => !c.disqualifying)
        .map(({ id, answer, salesforceId }) => {
          return {
            flag: id,
            answer: Array.isArray(answer) ? answer.join(', ') : answer,
            salesforceId,
          };
        });
      let multiQuestions = leadQuestions.filter((q) => q.multiSalesforce);
      questions.push(...mapMultiSalesforceToSingle(multiQuestions));

      let endpoint;
      if (['TTU', 'checkout'].includes(flowType)) {
        endpoint = '/v1/web/auth/sign-up';
      } else if (flowType === 'free-user-checkout') {
        endpoint =
          '/v1/free-user/checkout/information/' +
          window.sessionStorage.getItem('freeUserCheckoutToken');
      } else if (flowType === 'free-user-sign-up') {
        endpoint = '/v1/web/auth/free-user/sign-up';
      }

      if (endpoint) {
        const requestBody = {
          ...cleanBody,
          acceptedTerms: true,
          createLead: props.createLead || false,
          dateOfBirth: context.startInfo.dateOfBirth,
          entheaCompany: context.startInfo.entheaCompany,
          entry_origin: window.localStorage.entry_origin,
          flowCreatesLead: !props.redirectsToSchedule,
          formVariant: _formVariant,
          landingPageVariant: getLandingPageVariantString(
            window.localStorage.form_variant,
            window.localStorage.variant
          ),
          gclid: window.localStorage.gclid,
          gender: cleanBody.gender,
          leadQuestions: questions,
          practiceCode: window.sessionStorage.practiceCode ?? 'nuelife',
          recaptchaToken: 'empty',
          redFlagAnswers: flagQuestions.map((c) => ({
            flag: c.id,
            answer: c.answer,
          })),
          state: context.startInfo.state,
          trafficType: window.localStorage.traffic_source,
          utmCampaign: window.localStorage.utm_campaign,
          utmContent: window.localStorage.utm_content,
          utmMedium: window.localStorage.utm_medium,
          utmSource: window.localStorage.utm_source,
          utmTerm: window.localStorage.utm_term,
          zipCode: context.startInfo.zipCode,
        };

        await request({
          method: 'POST',
          path: endpoint,
          body: requestBody,
          headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
        });

        window?.gtag('event', 'submitted_account_form');
      }

      window?.gtag('event', 'conversion', {
        send_to: 'AW-743375642/WxoiCO7_iY4DEJqGvOIC',
      });

      trackOnce(
        props.createLead
          ? 'account_created_buy_flow'
          : 'account_created_talk_to_us'
      );

      const plan =
        props.plan || context.plans.find((c) => c.id === props.planId);
      if (plan) {
        window?.gtag('event', 'add_shipping_info', {
          currency: 'USD',
          value: plan.price,
          items: [
            {
              item_id: plan.id,
              item_name: plan.name,
              price: plan.price,
              quantity: 1,
            },
          ],
        });
      }

      setLoading(false);
      context.setAccount(body);

      props.onDone(fields);
    } catch (e) {
      captureError(e);
      setLoading(false);
      setError(e);
    }
  }

  function setField(name, value) {
    setFields(merge({}, fields, set({}, name, value)));
    setFieldsErrors({ ...fieldsErrors, [name]: undefined });
  }

  return (
    <>
      <div className="stepAccount">
        {error && (
          <ErrorDialog
            title="Something went wrong"
            message={error.message}
            onClose={() => setError(undefined)}
          />
        )}
        <Section
          color={props.flow === 'checkout' ? 'wellness' : 'changeyellow'}
          style={{ borderRadius: 0 }}>
          <div style={{ paddingBottom: '2em' }}></div>
          <SignupStepContainer
            header={
              <Header>
                <p className="bodycopy3">
                  Thank you for completing our form. Based on your responses, it
                  looks like the Nue Life program could be the right fit for
                  you. The next step is to set up an account and, afterwards,
                  schedule a brief phonecall with our Welcome Team.
                </p>
              </Header>
            }>
            <div style={{ paddingBottom: '2em' }}></div>
            <form>
              <FormInput
                flowType={flowType}
                fields={fields}
                fieldsErrors={fieldsErrors}
                setField={setField}
                sameAsBilling={sameAsBilling}
                setSameAsBilling={setSameAsBilling}
                showSmsOptIn={!requestPhoneNumber}
              />
              {flowType === 'free-user-sign-up' && (
                <>
                  <div
                    style={{
                      border: fieldsErrors.captcha ? '2px solid red' : 'none',
                      width: 'fit-content',
                    }}>
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_KEY}
                      onChange={onCaptchaCompletion}
                    />
                  </div>
                </>
              )}
              <Layout horizontal stackable>
                <Button
                  fluid
                  nueblue
                  className="submit"
                  loading={loading}
                  disabled={
                    loading || !fields.acceptedTerms || !fields.textMessageOptIn
                  }
                  onClick={handleOnSubmit}>
                  {(context.email !== '' && context.checkoutCode !== '') ||
                    flowType === 'free-user-checkout'
                    ? 'Proceed to payment'
                    : 'Create Account'}
                </Button>
                <div></div>
              </Layout>
            </form>
          </SignupStepContainer>
        </Section>
      </div>
    </>
  );
}

export default withLayout(Account);